// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Loader2, Menu as MenuIcon, Search, ChevronDown, ChevronRight, Info, RefreshCw, Lock } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { groupModules } from "../../lib/modPerms";

/* 菜单管理（/admin/menus）—— 系统管理 → 菜单管理
 *
 * 需求背景（本次只做「看」）：
 *   当前系统的侧栏菜单结构是**代码硬编码**的，分散在两处、必须同步维护：
 *     ① 前端 frontend/src/components/Layout.jsx 的 NAVDEF —— 决定侧栏显示什么（名称 / 图标 / 路径 / 分组 / 默认可见角色）
 *     ② 后端 backend/server.py 的 MODULE_CATALOG / SUBMODULE_CATALOG —— 决定权限矩阵里能勾哪些项、接口层的模块归属
 *   这导致「改个菜单名 / 调个分组 / 加一项」都要改代码 + 发版，且容易两处漏改（已有自检脚本 backend/tests/qa_nav_catalog.py 兜底）。
 *
 * 本次范围（按需求「留入口 + 显示出当前的菜单结构，不做其他操作，为以后修改打基础」）：
 *   **只读展示**当前菜单结构 —— 一级分组 → 菜单项 → 子栏目，含每项的路径、默认可见角色。
 *   不做任何写操作；页面里标明"可编辑能力规划中"，作为后续改造的落点与基线。
 *
 * 展示形态（走查修正 2026-10-13）：改成 **table 式树形** —— 单张表 + 表头，
 *   一级分组做成整行合并的组标题行，二级分组做成缩进的分组行，菜单项为普通行并逐级缩进；
 *   取代原先"每个分组一张独立卡片"的割裂样式（用户反馈不要那种）。
 *
 * 数据来源：复用现有 GET /roles/permissions（管理员会拿到完整 modules + subModules），
 *   不新开后端接口；分组逻辑复用 lib/modPerms 的 groupModules，与权限矩阵看到的层级完全一致。
 *
 * 后续（未实现）：把菜单名称 / 图标 / 排序 / 分组做成可后台配置并落库，路径保持代码固定；
 *   届时本页从"展示"升级为"编辑"，NAVDEF 改为「DB 覆盖 + 代码兜底」。
 */
export default function MenuManager() {
  const { lang, api } = useApp();
  const zh = lang === "zh";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/roles/permissions");
      setD(data);
    } catch (e) { setErr(e?.detailText || e.message); toast.error(e?.detailText || e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const roleLabel = (k) => {
    const r = (d?.baseRoles || []).find((x) => x.key === k);
    return r ? (zh ? r.zh : r.en) : k;
  };
  const rolesText = (roles) => {
    if (roles === "*") return zh ? "全部（分包商除外）" : "All (except subcontractor)";
    return (roles || []).map(roleLabel).join(" / ") || "—";
  };

  const groups = useMemo(() => (d?.modules ? groupModules(d.modules, zh) : []), [d, zh]);
  const subsByParent = useMemo(() => {
    const o = {};
    (d?.subModules || []).forEach((sm) => { (o[sm.parent] = o[sm.parent] || []).push(sm); });
    return o;
  }, [d]);

  const kw = q.trim().toLowerCase();
  const hit = (m) => !kw || [m.key, m.zh, m.en, m.sub?.zh, m.sub?.en].filter(Boolean).some((s) => String(s).toLowerCase().includes(kw));
  const filtered = useMemo(() => groups.map((g) => {
    if (!kw) return g;
    if (g.label.toLowerCase().includes(kw)) return g;
    const items = g.items.filter((m) => hit(m) || (subsByParent[m.key] || []).some(hit));
    const subs = g.subs.map((s) => ({ ...s, items: s.items.filter(hit) })).filter((s) => s.items.length || s.label.toLowerCase().includes(kw));
    return { ...g, items, subs };
  }).filter((g) => !kw || g.items.length || g.subs.length), [groups, kw, subsByParent]);

  const totalItems = d?.modules?.length || 0;
  const totalSubs = d?.subModules?.length || 0;

  return (
    <div className="space-y-4" data-testid="admin-menus-page">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MenuIcon className="h-6 w-6 text-brand-500" />{zh ? "菜单管理" : "Menu Management"}
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {zh ? "当前侧栏菜单结构总览（只读）。菜单编辑能力规划中，本页作为后续改造的基线。" : "Read-only overview of the current sidebar menu structure. Editing is planned; this page is the baseline."}
          </p>
        </div>
        <Button data-testid="menu-refresh" variant="outline" size="sm" className="gap-1" disabled={busy} onClick={load}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}{zh ? "刷新" : "Refresh"}
        </Button>
      </div>

      {/* 说明：菜单定义在代码里、两处同步；本页只读 */}
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-xs text-slate-600 leading-relaxed space-y-1" data-testid="menu-readonly-note">
        <p className="font-semibold text-indigo-900 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" />{zh ? "为什么现在只能看" : "Why read-only"}</p>
        <p>{zh
          ? "菜单结构目前写在代码里，分两处且必须同步：前端 Layout.jsx 的 NAVDEF（名称 / 图标 / 路径 / 分组）与后端 server.py 的 MODULE_CATALOG（权限矩阵可勾选项）。改名称、调分组、加菜单都要改代码并发版。"
          : "The menu structure is currently code-defined in two places that must stay in sync: Layout.jsx NAVDEF (name / icon / path / group) and server.py MODULE_CATALOG (what the permission matrix can toggle)."}</p>
        <p>{zh
          ? "后续会把「名称 / 图标 / 排序 / 分组」做成可后台配置并落库（菜单路径保持代码固定），届时本页升级为可编辑。"
          : "A later release will make name / icon / order / group configurable and persisted (paths stay code-fixed), turning this page into an editor."}</p>
        <p className="text-slate-400">{zh ? "默认可见角色 = 代码里的出厂默认；实际可见性还受「角色权限矩阵」与「账号级菜单权限」影响。" : "Default roles are the code-level factory default; actual visibility is further shaped by the role matrix and per-account overrides."}</p>
      </div>

      {err && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="menu-error">{zh ? `加载失败：${err}` : `Failed: ${err}`}</div>}

      {!d && !err && <div className="py-10 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>}

      {d && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500" data-testid="menu-summary">
            <span>{zh ? `${filtered.length} 个一级分组` : `${filtered.length} groups`}</span>
            <span>·</span>
            <span>{zh ? `${totalItems} 个菜单项` : `${totalItems} items`}</span>
            <span>·</span>
            <span>{zh ? `${totalSubs} 个子栏目` : `${totalSubs} sub-tabs`}</span>
            <div className="relative ml-auto w-full sm:w-64">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" />
              <Input data-testid="menu-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={zh ? "搜索菜单名称或路径…" : "Search name or path…"} className="h-7 pl-7 text-xs" />
            </div>
          </div>

          {/* table 式树形：单表 + 表头；分组为整行合并标题，菜单项逐级缩进 */}
          <div className="overflow-auto rounded-lg border border-slate-200 bg-white md:max-h-[calc(100vh-24rem)]">
            <ResponsiveTable className="w-full text-xs border-collapse" data-testid="menu-tree">
              <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left font-medium px-4 py-2 min-w-[260px]">{zh ? "菜单" : "Menu"}</th>
                  <th className="text-left font-medium px-3 py-2 w-[280px]">{zh ? "路径" : "Path"}</th>
                  <th className="text-left font-medium px-3 py-2 w-[220px]">{zh ? "默认可见角色" : "Default roles"}</th>
                  <th className="text-left font-medium px-3 py-2">{zh ? "子栏目" : "Sub-tabs"}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g) => {
                  const isOpen = !collapsed[g.label];
                  const count = g.items.length + g.subs.reduce((n, s) => n + s.items.length, 0);
                  return (
                    <React.Fragment key={g.label}>
                      {/* 一级分组：整行合并标题行 */}
                      <tr className="bg-slate-100/80 border-t border-slate-200" data-testid={`menu-group-${g.label}`}>
                        <td colSpan={4} className="px-2 py-0">
                          <button type="button" data-testid={`menu-group-toggle-${g.label}`} onClick={() => setCollapsed((c) => ({ ...c, [g.label]: !c[g.label] }))}
                            className="max-md:min-h-11 max-md:min-w-11 w-full flex items-center gap-2 px-2 py-2 text-left hover:bg-slate-200/50 rounded">
                            {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
                            <span className="font-bold text-slate-800">{g.label}</span>
                            <span className="text-[11px] text-slate-400 font-normal">{count} {zh ? "项" : "items"}</span>
                          </button>
                        </td>
                      </tr>
                      {isOpen && g.items.map((m) => <MenuRow key={m.key} m={m} zh={zh} rolesText={rolesText} subs={subsByParent[m.key] || []} />)}
                      {isOpen && g.subs.map((s) => (
                        <React.Fragment key={s.label}>
                          {/* 二级分组（对应侧栏可折叠组，如「用户管理」）：缩进的分组行 */}
                          <tr className="bg-slate-50/80 border-t border-slate-100" data-testid={`menu-subhead-${s.label}`}>
                            <td colSpan={4} className="pl-9 pr-4 py-1.5 text-[11px] font-semibold text-slate-600">
                              <span className="text-slate-300 mr-1">└</span>{s.label}
                              <span className="ml-1.5 font-normal text-slate-400">{s.items.length} {zh ? "项" : "items"}</span>
                            </td>
                          </tr>
                          {s.items.map((m) => <MenuRow key={m.key} m={m} zh={zh} rolesText={rolesText} subs={subsByParent[m.key] || []} indented />)}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}
                {!filtered.length && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-400">{zh ? "没有匹配的菜单" : "No matching menus"}</td></tr>
                )}
              </tbody>
            </ResponsiveTable>
          </div>
        </>
      )}
    </div>
  );
}

/* 单个菜单项行：名称（缩进）· 路径 · 默认可见角色 · 子栏目（栏目级权限项） */
function MenuRow({ m, zh, rolesText, subs, indented = false }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`menu-item-${m.key}`}>
      <td className={`px-4 py-2 text-slate-800 ${indented ? "pl-12" : "pl-6"}`}>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-slate-300">{indented ? "└" : "·"}</span>
          {zh ? m.zh : m.en}
          {m.sub && <span className="text-[10px] px-1 rounded bg-indigo-50 text-indigo-700">{zh ? "分组" : "group"}</span>}
          <span className="text-[10px] text-slate-300 inline-flex items-center gap-0.5" title={zh ? "菜单定义在代码里，本页只读" : "Code-defined; read-only"}>
            <Lock className="h-2.5 w-2.5" />
          </span>
        </span>
      </td>
      <td className="px-3 py-2"><code className="text-[11px] text-slate-400">{m.key}</code></td>
      <td className="px-3 py-2 text-[11px] text-slate-500">{rolesText(m.roles)}</td>
      <td className="px-3 py-2 text-[11px] text-slate-500">
        {subs.length === 0 ? <span className="text-slate-300">—</span> : (
          <span title={subs.map((sm) => (zh ? sm.zh : sm.en)).join(" / ")} className="line-clamp-1">
            {subs.map((sm) => (
              <span key={sm.key} data-testid={`menu-sub-${sm.key}`} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{zh ? sm.zh : sm.en}</span>
            ))}
          </span>
        )}
      </td>
    </tr>
  );
}
